import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { resolvePlayer } from "./displayHelpers";
import { countQualified, poolProgress } from "./navigation";
import {
  interactiveSurfaceSx,
  isDarkMode,
} from "./resultatsTheme";
import type { PlayerDirectory, Pool } from "./types";

interface PoolSummaryCardProps {
  pool: Pool;
  players: PlayerDirectory;
  selected?: boolean;
  highlightedLicence?: string | null;
  showResults?: boolean;
  onSelect: () => void;
}

export default function PoolSummaryCard({
  pool,
  players,
  selected = false,
  highlightedLicence = null,
  showResults = false,
  onSelect,
}: PoolSummaryCardProps): React.ReactElement {
  const theme = useTheme();
  const isDark = isDarkMode(theme);
  const progress = poolProgress(pool);
  const qualified = countQualified(pool);
  const containsHighlight =
    Boolean(highlightedLicence) &&
    pool.standings.some((s) => s.licence === highlightedLicence);

  const orderedStandings = [...pool.standings].sort((a, b) => {
    const ra = a.rank ?? 999;
    const rb = b.rank ?? 999;
    return ra - rb;
  });

  const emphasized = selected || containsHighlight;

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        ...interactiveSurfaceSx(theme),
        borderColor: emphasized
          ? alpha(theme.palette.secondary.main, isDark ? 0.65 : 0.55)
          : undefined,
        boxShadow: emphasized
          ? `0 0 0 1px ${alpha(theme.palette.secondary.main, 0.35)}`
          : undefined,
      }}
    >
      <CardActionArea
        onClick={onSelect}
        sx={{
          height: "100%",
          borderRadius: "inherit",
          alignItems: "stretch",
        }}
      >
        <CardContent sx={{ p: 1.85, "&:last-child": { pb: 1.85 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              alignItems: "baseline",
              mb: 1.1,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                fontSize: "1rem",
                letterSpacing: "-0.015em",
                lineHeight: 1.2,
              }}
            >
              {pool.label}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700, flexShrink: 0, letterSpacing: "0.02em" }}
            >
              {showResults && progress
                ? `${progress.completed}/${progress.total}`
                : `${pool.standings.length} j.`}
            </Typography>
          </Box>

          <Box
            component="ul"
            sx={{
              m: 0,
              p: 0,
              listStyle: "none",
              display: "flex",
              flexDirection: "column",
              gap: 0.35,
            }}
          >
            {orderedStandings.map((standing, index) => {
              const identity = resolvePlayer(standing.licence, players);
              const isHighlight = standing.licence === highlightedLicence;
              const isQualified = standing.qualification === "QUALIFIED";
              return (
                <Box
                  component="li"
                  key={`${standing.licence ?? "u"}-${index}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: showResults
                      ? "1.1rem minmax(0, 1fr) auto"
                      : "minmax(0, 1fr)",
                    gap: 0.7,
                    alignItems: "baseline",
                    px: 0.65,
                    py: 0.35,
                    borderRadius: 1.5,
                    bgcolor: isHighlight
                      ? alpha(
                          theme.palette.secondary.main,
                          isDark ? 0.24 : 0.14
                        )
                      : isQualified
                        ? alpha(
                            theme.palette.success.main,
                            isDark ? 0.16 : 0.1
                          )
                        : "transparent",
                  }}
                >
                  {showResults && (
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.72rem",
                        fontVariantNumeric: "tabular-nums",
                        color: "text.secondary",
                      }}
                    >
                      {standing.rank ?? index + 1}
                    </Typography>
                  )}
                  <Typography
                    component="span"
                    title={identity.displayName}
                    sx={{
                      fontSize: "0.84rem",
                      fontWeight:
                        standing.rank === 1 && showResults ? 700 : 500,
                      color: "text.primary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {identity.displayName}
                  </Typography>
                  {showResults && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        color: "text.secondary",
                        fontVariantNumeric: "tabular-nums",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {standing.wins}–{standing.losses}
                    </Typography>
                  )}
                </Box>
              );
            })}
            {orderedStandings.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Composition non disponible
              </Typography>
            )}
          </Box>

          {qualified > 0 && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 1,
                fontWeight: 700,
                color: "success.main",
                letterSpacing: "0.01em",
              }}
            >
              {qualified} qualifié{qualified > 1 ? "s" : ""}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
